/**
 * The image-geometry codec: the `=NN%` display width Niobe stores in a markdown image's
 * TITLE slot.
 *
 *     ![Site plan](/v1/blobs/<sha256> "=60% Site plan")
 *
 * WHY THE TITLE SLOT. A part's CRDT is a markdown Y.Text and nothing else. RichEditor
 * re-serialises the WHOLE ProseMirror doc to markdown on every transaction, and the server
 * re-normalises it through the same stack (backend/adapters/markdown/canonical.ts), so any
 * geometry markdown cannot express is erased on the next reload, remote edit or
 * canonicalisation pass. `title` is a declared attribute of the base Image node and BOTH
 * markdown stacks already round-trip it (`defaultMarkdownSerializer.nodes.image`), so the
 * token survives with no serialiser change on either side.
 *
 * WHY A PERCENTAGE. A pixel number is meaningless once the document is rendered onto A4 by
 * an export, or read on another screen. The width is a fraction of the text column, and it
 * is the ONLY stored number — the height always follows the aspect ratio.
 *
 * THE GRAMMAR is whitespace-separated words:
 *   `=<N>%`          the display width. 5–100, at most one decimal.
 *   `crop:<a,b,c,d>` RESERVED for the follow-up crop task. Parsed and preserved verbatim so
 *                    that task needs no migration; nothing reads it yet.
 *   anything else    human title text, kept in order.
 *
 * A word that ALMOST looks like a token (`=600%`, `=6.05%`) is not one: it stays human title
 * text and the image falls back to its natural size. That degradation is deliberate. Two
 * people resizing the same image at once can interleave digits inside the one markdown line
 * (Yjs merges the Y.Text, not the token), and "no geometry, text preserved" is the only
 * failure mode that neither throws nor destroys what someone typed. Re-committing a resize
 * repairs the geometry.
 *
 * The one thing a round trip does not preserve byte-for-byte is RUNS OF WHITESPACE inside the
 * human title: the codec works on words, so "Site  plan" comes back "Site plan". A markdown
 * image title is a single line of prose, so that is a normalisation, not a loss — and it buys
 * an implementation simple enough to keep identical on both sides.
 *
 * Parity: backend/domain/image-geometry.ts holds an identical copy (the web app cannot import
 * backend source), mechanically pinned by shared/image-geometry.fixture.json, which BOTH
 * sides assert against. Keep the two in sync — the same arrangement as diff.ts.
 */

/** Narrowest width a member can set, in percent of the text column. */
export const WIDTH_MIN = 5
/** Widest: the full text column. */
export const WIDTH_MAX = 100

/** A width word: `=` then 1–3 digits with at most one decimal, then `%`. Nothing else. */
const WIDTH_WORD = /^=(\d{1,3}(?:\.\d)?)%$/
/** Reserved geometry words — recognised, carried through untouched, never shown as text. */
const RESERVED_WORD = /^crop:\S*$/

/** The three things a title slot can hold, separated. */
export interface ImageTitleParts {
  /** Display width in percent of the text column, or null when unset (natural size). */
  widthPct: number | null
  /** The human-readable part of the title. '' when there is none. */
  title: string
  /** Recognised-but-unhandled geometry words (today: `crop:…`), space-joined, verbatim. */
  rest: string
}

/**
 * Round to one decimal and clamp into [WIDTH_MIN, WIDTH_MAX]. Returns null for anything that
 * is not a finite number, so a caller can pass a parsed field straight in.
 */
export function clampWidthPct(n: number): number | null {
  if (!Number.isFinite(n)) return null
  return Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, Math.round(n * 10) / 10))
}

/** Split an image `title` attribute into its width, its reserved words and its human text. */
export function parseImageTitle(raw: string | null | undefined): ImageTitleParts {
  const words = (raw ?? '').split(/\s+/).filter((w) => w !== '')
  let widthPct: number | null = null
  const rest: string[] = []
  const title: string[] = []
  for (const word of words) {
    const width = WIDTH_WORD.exec(word)
    // Only the FIRST valid width is geometry. A second one is left as text rather than
    // silently dropped, which also keeps the codec idempotent over its own output.
    if (width && widthPct === null) {
      const value = Number(width[1])
      if (value >= WIDTH_MIN && value <= WIDTH_MAX) {
        widthPct = value
        continue
      }
    }
    if (RESERVED_WORD.test(word)) {
      rest.push(word)
      continue
    }
    title.push(word)
  }
  return { widthPct, title: title.join(' '), rest: rest.join(' ') }
}

/**
 * Build the title attribute from its parts — geometry first, human text last. Returns null
 * when there is nothing to say, so the caller clears the attribute and the markdown goes back
 * to a bare `![alt](src)`.
 */
export function formatImageTitle(parts: Partial<ImageTitleParts>): string | null {
  const width = parts.widthPct == null ? null : clampWidthPct(parts.widthPct)
  const words: string[] = []
  if (width !== null) words.push(`=${width}%`)
  const rest = (parts.rest ?? '').trim()
  if (rest !== '') words.push(rest)
  const title = (parts.title ?? '').trim()
  if (title !== '') words.push(title)
  return words.length === 0 ? null : words.join(' ')
}
